// src/pages/SideBar.jsx
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import LanguageSelector from "../components/LanguageSelector";
import { FaWhatsapp } from "react-icons/fa";
import SidebarProfile from "../components/SidebarProfile";

/* ─── CSS for the gaming drawer ─── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Exo+2:wght@400;500&display=swap');

  /* ── Sidebar backdrop blur overlay ── */
  .sidebar-overlay {
    background: rgba(8, 0, 24, 0.75);
    backdrop-filter: blur(3px);
  }

  /* ── Drawer panel ── */
  .sidebar-panel {
    background: #080018;
    border-left: 1px solid rgba(255, 146, 0, 0.3);
    box-shadow: -8px 0 40px rgba(255, 146, 0, 0.12), -2px 0 0 rgba(255, 146, 0, 0.5);
    font-family: 'Exo 2', sans-serif;
    overflow: hidden;
  }

  /* HEX grid background texture */
  .sidebar-panel::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.04;
    background-image:
      linear-gradient(60deg, rgba(255,146,0,0.8) 1px, transparent 1px),
      linear-gradient(-60deg, rgba(255,146,0,0.8) 1px, transparent 1px),
      linear-gradient(rgba(255,146,0,0.8) 1px, transparent 1px);
    background-size: 30px 52px, 30px 52px, 30px 52px;
    pointer-events: none;
    z-index: 0;
  }

  /* subtle top glow */
  .sidebar-panel::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, #FF9200, transparent);
    box-shadow: 0 0 12px rgba(255,146,0,0.7);
  }

  .sidebar-panel > * { position: relative; z-index: 1; }

  /* ── section header ── */
  .sidebar-header-text {
    font-family: 'Rajdhani', sans-serif;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: rgba(255, 146, 0, 0.7);
  }

  /* ── circuit-line divider ── */
  .circuit-divider {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    margin: 6px 0;
  }
  .circuit-divider::before,
  .circuit-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,146,0,0.35));
  }
  .circuit-divider::after {
    background: linear-gradient(270deg, transparent, rgba(255,146,0,0.35));
  }
  .circuit-dot {
    width: 4px; height: 4px;
    border-radius: 50%;
    background: rgba(255,146,0,0.6);
    box-shadow: 0 0 6px rgba(255,146,0,0.8);
  }

  /* ── nav items ── */
  .nav-item {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    border-radius: 4px;
    transition: background 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    border-left: 2px solid transparent;
    cursor: pointer;
    text-decoration: none;
    width: 100%;
    background: transparent;
    font-family: 'Exo 2', sans-serif;
  }
  .nav-item:hover,
  .nav-item:focus {
    background: rgba(255, 146, 0, 0.07);
    border-left-color: #FF9200;
    box-shadow: inset 0 0 20px rgba(255,146,0,0.05), 2px 0 0 rgba(255,146,0,0.2);
    outline: none;
  }
  .nav-item-icon {
    flex-shrink: 0;
    width: 20px; height: 20px;
    display: flex; align-items: center; justify-content: center;
    opacity: 0.8;
    transition: opacity 0.15s, filter 0.15s;
  }
  .nav-item:hover .nav-item-icon {
    opacity: 1;
    filter: drop-shadow(0 0 4px rgba(255,146,0,0.7));
  }
  .nav-item-label {
    margin-left: 12px;
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(255,255,255,0.75);
    letter-spacing: 0.03em;
    transition: color 0.15s;
  }
  .nav-item:hover .nav-item-label {
    color: rgba(255,255,255,1);
  }

  /* ── logout button ── */
  .logout-btn {
    border-left-color: transparent;
  }
  .logout-btn:hover {
    background: rgba(255, 60, 60, 0.08);
    border-left-color: #ff4444;
    box-shadow: inset 0 0 20px rgba(255,60,60,0.06);
  }
  .logout-btn:hover .nav-item-label {
    color: #ff7070;
  }
  .logout-btn:hover .nav-item-icon {
    filter: drop-shadow(0 0 4px rgba(255,60,60,0.7));
  }

  /* ── whatsapp item ── */
  .wa-icon {
    font-size: 1.3rem;
    color: #25d366;
    flex-shrink: 0;
    transition: filter 0.15s;
  }
  .nav-item:hover .wa-icon {
    filter: drop-shadow(0 0 5px rgba(37,211,102,0.8));
  }

  /* scrollable nav list */
  .nav-list {
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,146,0,0.3) transparent;
  }
  .nav-list::-webkit-scrollbar { width: 3px; }
  .nav-list::-webkit-scrollbar-track { background: transparent; }
  .nav-list::-webkit-scrollbar-thumb { background: rgba(255,146,0,0.3); border-radius: 2px; }
`;

export default function SideBar({ toggleSidebar, isSidebarOpen }) {
  const { userData, logout } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const message = userData
    ? `${t("supportMessagePrefix")} My Name is ${userData?.user_name}, User ID: ${userData?.id}, Email: ${userData?.user_email}, `
    : "";
  const encodedMessage = userData ? encodeURIComponent(message) : "";

  return (
    <>
      <style>{css}</style>

      {/* ── Overlay ── */}
      <div
        className={`sidebar-overlay fixed inset-0 z-40 transition-opacity duration-300 ${
          isSidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleSidebar}
      />

      {/* ── Drawer Panel ── */}
      <div
        className={`sidebar-panel fixed top-0 right-0 h-full w-2/3 max-w-xs max-[450px]:w-4/5 z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* header */}
        <div className="flex items-center justify-between px-4 pt-5 pb-3">
          <span className="sidebar-header-text">{t("connectAndCompete")}</span>
          {/* close X */}
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center w-7 h-7 rounded"
            style={{
              border: '1px solid rgba(255,146,0,0.3)',
              background: 'rgba(255,146,0,0.07)',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1,
            }}
            aria-label="Close menu"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* profile card */}
        {userData && <SidebarProfile />}

        {/* ── Nav list ── */}
        <div className="nav-list flex-1 py-3">

          {userData && (
            <>
              {/* section label */}
              <div className="sidebar-header-text px-4 pb-2 mt-1" style={{ fontSize: '0.6rem' }}>
                Account
              </div>

              {/* Edit Profile */}
              <Link
                to={{ pathname: "/updateProfile", state: { profile: userData } }}
                className="nav-item"
              >
                <span className="nav-item-icon">
                  <svg viewBox="0 0 42 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                    <g clipPath="url(#cp1)">
                      <path d="M21 24C24.1826 24 27.2348 22.7357 29.4853 20.4853C31.7357 18.2348 33 15.1826 33 12C33 8.8174 31.7357 5.76516 29.4853 3.51472C27.2348 1.26428 24.1826 0 21 0C17.8174 0 14.7652 1.26428 12.5147 3.51472C10.2643 5.76516 9 8.8174 9 12C9 15.1826 10.2643 18.2348 12.5147 20.4853C14.7652 22.7357 17.8174 24 21 24ZM16.7156 28.5C7.48125 28.5 0 35.9812 0 45.2156C0 46.7531 1.24688 48 2.78438 48H39.2156C40.7531 48 42 46.7531 42 45.2156C42 35.9812 34.5188 28.5 25.2844 28.5H16.7156Z" fill="#FF8C00"/>
                    </g>
                    <defs><clipPath id="cp1"><rect width="42" height="48" fill="white"/></clipPath></defs>
                  </svg>
                </span>
                <span className="nav-item-label">{t("editProfile")}</span>
              </Link>

              {/* Admin + Mediator items */}
              {(userData.user_type === 1 || userData.user_type === 3) && (
                <>
                  {/* Manage Members */}
                  <Link
                    to={{ pathname: "/add-member", state: { profile: userData } }}
                    className="nav-item"
                  >
                    <span className="nav-item-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#FF8C00" width="18" height="18">
                        <path d="M485.22,80.604H26.78C12.013,80.604,0,92.615,0,107.382v297.229c0,14.767,12.013,26.786,26.78,26.786h458.44 c14.767,0,26.78-12.019,26.78-26.786V107.382C512,92.615,499.987,80.604,485.22,80.604z M132.17,283.787v-4.472 c0-2.298-0.38-4.566-1.359-7.174c-0.188-0.248-18.793-25.357-18.793-48.059c0-27.05,16.988-46.685,40.394-46.685 s40.394,19.635,40.394,46.685c0,22.702-18.604,47.811-19.022,48.494c-0.751,2.166-1.132,4.433-1.132,6.739v4.472 c0,6.18,3.628,11.84,9.307,14.449l30.316,12.384c6.584,3.028,11.285,9.084,12.558,16.048l1.431,18.416H78.56l1.407-18.253 c1.294-7.128,5.998-13.184,12.518-16.18l30.442-12.446C128.542,295.627,132.17,289.967,132.17,283.787z M429.318,306.396v29.557 H266.745v-29.557H429.318z M429.318,242.793v29.558H266.745v-29.558H429.318z M231.481,208.748v-29.557h197.836v29.557H231.481z"/>
                      </svg>
                    </span>
                    <span className="nav-item-label">{t("manageMembers")}</span>
                  </Link>

                  {/* Recharge */}
                  <Link
                    to={{ pathname: "/recharge", state: { profile: userData } }}
                    className="nav-item"
                  >
                    <span className="nav-item-icon">
                      <svg viewBox="69.8655 71.9463 339.481 339.4806" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                        <g transform="matrix(0.6630480289459229,0,0,0.6630480289459229,69.86547088623047,71.94631958007815)">
                          <path fill="#EFB832" d="M256,512C114.841,512,0,397.159,0,256S114.841,0,256,0s256,114.841,256,256S397.159,512,256,512z"/>
                          <g transform="matrix(1.190054,0,0,1.190054,-45.374573,225.235107)">
                            <path d="M 211.034 99.593 C 211.254 99.943 211.784 99.723 211.704 99.323 L 185.544 -30.727 C 178.904 -28.037 169.044 -16.087 166.294 -12.637 C 164.904 -10.887 164.044 -7.817 164.144 -4.467 C 164.184 -3.307 164.324 -2.137 164.574 -1.007 C 167.754 13.223 176.554 44.703 211.034 99.593 Z" fill="rgb(255,146,0)"/>
                            <path d="M 248.254 33.883 C 248.254 33.883 256.364 -21.147 259.854 -24.107 C 262.004 -25.927 309.584 -27.437 309.584 -27.437 L 309.154 25.973 C 309.644 27.573 309.064 29.243 307.814 30.353 C 243.624 87.493 221.044 102.233 214.994 110.713 C 214.634 111.223 215.144 111.963 215.714 111.703 C 267.524 88.273 320.544 53.003 339.274 38.663 C 341.604 36.883 341.314 33.983 341.344 31.053 C 341.824 -8.777 344.194 -47.257 338.934 -54.877 C 338.384 -55.677 337.554 -56.257 336.614 -56.497 C 295.304 -66.957 245.104 -61.197 233.844 -58.067 C 232.204 -57.607 230.954 -56.307 230.554 -54.657 C 216.344 3.093 223.664 70.123 224.734 81.373 C 224.784 81.903 225.414 82.143 225.814 81.793 L 290.464 25.773 C 292.164 24.253 292.284 21.623 290.734 19.953 L 267.434 -14.657 C 266.324 -15.777 264.844 -14.557 265.274 -13.037 C 270.104 4.153 273.374 16.023 273.374 16.023 L 248.254 33.883 Z" fill="rgb(18,2,57)"/>
                          </g>
                        </g>
                      </svg>
                    </span>
                    <span className="nav-item-label">{t("recharge")}</span>
                  </Link>

                  {/* Reports */}
                  <Link
                    to={{ pathname: "/report", state: { profile: userData } }}
                    className="nav-item"
                  >
                    <span className="nav-item-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="#FF8C00" viewBox="0 0 36 36" width="18" height="18">
                        <rect x="6.48" y="18" width="5.76" height="11.52" rx="1" ry="1"/>
                        <rect x="15.12" y="6.48" width="5.76" height="23.04" rx="1" ry="1"/>
                        <rect x="23.76" y="14.16" width="5.76" height="15.36" rx="1" ry="1"/>
                      </svg>
                    </span>
                    <span className="nav-item-label">{t("reports")}</span>
                  </Link>
                </>
              )}

              {/* Partner-only Management */}
              {(userData.user_type === 1 || userData.user_type === 4) && (
                <Link
                  to={{ pathname: "/rewards", state: { profile: userData } }}
                  className="nav-item"
                >
                  <span className="nav-item-icon">
                    <svg viewBox="0 0 24 24" fill="#FF8C00" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2l2.39 4.85L20 7.24l-3.5 3.42L17.8 18 12 15.27 6.2 18l1.3-7.34L4 7.24l5.61-.39L12 2z"/>
                    </svg>
                  </span>
                  <span className="nav-item-label">{t("manageRewards")}</span>
                </Link>
              )}
            </>
          )}

          {/* circuit divider */}
          <div className="circuit-divider">
            <span className="circuit-dot" />
          </div>

          <div className="sidebar-header-text px-4 pb-2 mt-1" style={{ fontSize: '0.6rem' }}>
            Settings
          </div>

          {/* Language */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="nav-item"
          >
            <span className="nav-item-icon">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                <g clipPath="url(#cp2)">
                  <path d="M4.84688 27.6656L7.81875 28.2562C8.55937 28.4062 9.31875 28.1719 9.85312 27.6375L11.2969 26.1937C12.3844 25.1062 14.2125 25.4062 14.8969 26.775L15.7687 28.5094C16.2188 29.4094 17.1375 29.9812 18.15 29.9812C19.575 29.9812 20.5969 28.6125 20.1844 27.2438L19.6219 25.3781C19.1906 23.9344 20.2688 22.4813 21.7781 22.4813H21.9938C23.25 22.4813 24.4219 21.8531 25.1156 20.8125L26.1188 19.3031C26.6438 18.5062 26.6156 17.4656 26.0437 16.7062L24.5344 14.6906C23.5688 13.4062 24.225 11.55 25.7906 11.1562L27.3844 10.7531C28.0875 10.575 28.6594 10.0781 28.9313 9.40313L30.4688 5.56875C28.4438 4.88438 26.2687 4.5 24 4.5C13.2281 4.5 4.5 13.2281 4.5 24C4.5 25.2563 4.62188 26.4844 4.84688 27.6656ZM43.0687 28.0969C42.7875 28.0688 42.5062 28.0875 42.225 28.1719L40.7438 28.5844C40.1156 28.7625 39.45 28.5 39.1031 27.9563L38.9156 27.6656C38.3531 26.7844 37.3781 26.25 36.3281 26.25C35.2781 26.25 34.3031 26.7844 33.7406 27.6656L33.1687 28.5563C33.0375 28.7625 32.85 28.9406 32.6344 29.0531L29.25 30.9469C27.5531 31.8938 26.8594 33.9844 27.6562 35.7562L28.1719 36.9187C28.9781 38.7188 31.05 39.5906 32.9062 38.8969L33.15 38.8031C34.0875 38.4562 35.1469 38.5969 35.9531 39.1875L36.0938 39.2906C39.5812 36.525 42.1031 32.5969 43.0687 28.0875V28.0969ZM0 24C0 10.7453 10.7453 0 24 0C37.2547 0 48 10.7453 48 24C48 37.2547 37.2547 48 24 48C10.7453 48 0 37.2547 0 24Z" fill="#FF8C00"/>
                </g>
                <defs><clipPath id="cp2"><rect width="48" height="48" fill="white"/></clipPath></defs>
              </svg>
            </span>
            <span className="nav-item-label">{t("language")}</span>
          </button>

          {/* Privacy / Subscriptions */}
          <Link to="/subscriptions" className="nav-item">
            <span className="nav-item-icon">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                <path d="M24 0C24.4313 0 24.8625 0.09375 25.2563 0.271875L42.9094 7.7625C44.9719 8.63438 46.5094 10.6688 46.5 13.125C46.4532 22.425 42.6282 39.4406 26.475 47.175C24.9094 47.925 23.0907 47.925 21.525 47.175C5.37192 39.4406 1.54692 22.425 1.50004 13.125C1.49067 10.6688 3.02817 8.63438 5.09067 7.7625L22.7532 0.271875C23.1375 0.09375 23.5688 0 24 0ZM24 6.2625V41.7094C36.9375 35.4375 40.4157 21.5719 40.5 13.2563L24 6.2625Z" fill="#FF8C00"/>
              </svg>
            </span>
            <span className="nav-item-label">{t("privacy")}</span>
          </Link>

          {/* Customer Support */}
          <Link
            to={`https://wa.me/97433016148?text=${encodedMessage}`}
            className="nav-item"
          >
            <FaWhatsapp className="wa-icon" />
            <span className="nav-item-label">{t("customerSupport")}</span>
          </Link>

          {/* circuit divider */}
          <div className="circuit-divider">
            <span className="circuit-dot" />
          </div>

          {/* Logout */}
          {userData && (
            <button onClick={logout} className="nav-item logout-btn">
              <span className="nav-item-icon">
                <svg viewBox="0 0 48 42" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                  <path d="M47.1188 23.1188C48.2906 21.9469 48.2906 20.0438 47.1188 18.8719L35.1188 6.87188C33.9469 5.7 32.0437 5.7 30.8719 6.87188C29.7 8.04375 29.7 9.94688 30.8719 11.1188L37.7531 18H18C16.3406 18 15 19.3406 15 21C15 22.6594 16.3406 24 18 24H37.7531L30.8719 30.8812C29.7 32.0531 29.7 33.9563 30.8719 35.1281C32.0437 36.3 33.9469 36.3 35.1188 35.1281L47.1188 23.1281V23.1188ZM15 6C16.6594 6 18 4.65938 18 3C18 1.34062 16.6594 0 15 0H9C4.03125 0 0 4.03125 0 9V33C0 37.9688 4.03125 42 9 42H15C16.6594 42 18 40.6594 18 39C18 37.3406 16.6594 36 15 36H9C7.34062 36 6 34.6594 6 33V9C6 7.34062 7.34062 6 9 6H15Z" fill="#FF4444"/>
                </svg>
              </span>
              <span className="nav-item-label" style={{ color: 'rgba(255,120,120,0.8)' }}>
                {t("logout")}
              </span>
            </button>
          )}
        </div>

        {/* version tag at bottom */}
        <div
          className="px-4 pb-4 text-center"
          style={{
            fontFamily: "'Exo 2', sans-serif",
            fontSize: '0.6rem',
            letterSpacing: '0.15em',
            color: 'rgba(255,146,0,0.25)',
          }}
        >
          VIGOR · GAMER NETWORK
        </div>
      </div>

      <LanguageSelector isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}